<template>
  <a
    v-if="item.link"
    :id="`button${item._uid}`"
    :ref="item._uid || null"
    v-editable="item && !item.isGlobal"
    :href="item.link"
    :target="item.openInNewTab ? '_blank' : '_self'"
    class="group inline-flex items-center justify-center font-medium focus:outline-solid"
    :class="[
      validatedClasses,
      item.iconSide === 'right' ? 'flex-row-reverse' : '',
    ]"
    @click="(e) => handleLinkClick(e)"
  >
    <RawSvgIcon
      v-if="item.icon"
      :svg="item.icon"
      :class="item.iconSide === 'right' ? 'ml-2' : 'mr-2'"
      :classes="iconClasses"
    />
    {{ item.label }}
  </a>
  <button
    v-else
    :id="`button${item._uid}`"
    :ref="item._uid || null"
    v-editable="item && !item.isGlobal"
    class="group inline-flex items-center justify-center font-medium"
    :class="[
      validatedClasses,
      item.iconSide === 'right' ? 'flex-row-reverse' : '',
    ]"
    @click="handleClick"
  >
    <RawSvgIcon
      v-if="item.icon"
      :svg="item.icon"
      :class="item.iconSide === 'right' ? 'ml-2' : 'mr-2'"
      :classes="iconClasses"
    />
    {{ item.label }}
  </button>
</template>

<script setup>
import { computed } from 'vue'
import ctaEventBus from '../ctaEventBus.js'
import handleHashLink from '../utils/handleHashLink'
import RawSvgIcon from './RawSvgIcon.vue'
import { useColorPalette } from '../composables/colorPalette'
import { useHead } from '@unhead/vue'
import { usePageContext } from '../renderer/usePageContext'

const props = defineProps({
  blok: {
    type: Object,
    default: () => {},
  },
  item: {
    type: Object,
    default: () => {},
  },
})

const { colorCustomClass } = useColorPalette()

const backgroundColor = computed(() => {
  // DEPRECATED PROPERTY
  return props.item.color || 'primary'
})
const backgroundVariant = computed(() => {
  // DEPRECATED PROPERTY
  return parseInt(props.item.bgVariant) || 500
})
const textColor = computed(() => {
  // DEPRECATED PROPERTY
  if (props.item.textColor) {
    return props.item.textColor
  }

  if (backgroundColor.value === 'white' || backgroundVariant.value <= 200) {
    return 'gray'
  } else {
    return 'white'
  }
})
const textVariant = computed(() => {
  // DEPRECATED PROPERTY
  return parseInt(props.item.textVariant) || 500
})
const borderVisible = computed(() => {
  return props.item.borderVisible || false
})
const iconClasses = computed(() => {
  const classes = {
    xs: 'text-current w-4 h-4',
    sm: 'text-current w-4 h-4',
    md: 'text-current w-5 h-5',
    lg: 'text-current w-6 h-6',
    xl: 'text-current w-6 h-6',
  }

  const size = props.item.size

  return classes[size] || classes['lg']
})
const sizeClasses = computed(() => {
  const classes = {
    xs: 'px-2.5 py-1.5 text-xs',
    sm: 'px-3 py-2 text-sm leading-4',
    md: 'px-4 py-2 text-sm',
    lg: 'px-4 py-2 text-base',
    xl: 'px-6 py-3 text-base',
  }

  const size = props.item.size

  return classes[size] || classes['lg']
})
const styleClasses = computed(() => {
  const styleClasses = {
    default: `btn-style-default`,
    rounded: `btn-style-rounded`,
    square: `btn-style-square`,
    outline: `btn-style-outline`,
    'outline-rounded': `btn-style-outline-rounded`,
    primary: `btn-base btn-style-primary`,
    secondary: `btn-base btn-style-secondary`,
    white: `btn-base btn-style-white`,
    transparent: `btn-base btn-style-transparent`,
    'white-colored': `btn-base btn-style-white-colored`,
  }

  const style = props.item.style || 'default'
  return `${styleClasses[style]} ${legacyBtnClasses(style)}`
})
const validatedClasses = computed(() => {
  const classes = `${sizeClasses.value} ${styleClasses.value}`
  // Remove variants for black/white color. Eg: text-black-400 should be text-black
  return classes.replace(/(black|white)-\d+/g, '$1')
})

function handleClick() {
  submitSoftApplyEvent()
  ctaEventBus.$emit(props.item.actionIntent)
}
function handleLinkClick(event) {
  submitSoftApplyEvent()
  handleHashLink(event, event.currentTarget)
}
function submitSoftApplyEvent() {
  if (window.dataLayer && props.item.actionIntent === 'vacancy-apply') {
    window.dataLayer.push({
      event: 'jrUniversalSoftApply',
    })
  }
}
function legacyBtnClasses(style) {
  // We still add these in case there are custom CSS rules using them in a site
  // These classes are not the ones actually being used to style the button
  const baseClasses = `transition-all transition duration-150 ease-in-out shadow-sm`

  // Variables used to improve readability
  const bgColor = backgroundColor.value
  const bgVariant = backgroundVariant.value
  const tColor = textColor.value
  const tVariant = textVariant.value

  const hoverColor = bgColor === 'white' ? 'gray' : bgColor
  const hoverVariant =
    bgColor === 'white' ? 100 : Math.min(backgroundVariant.value + 100, 900)

  const borderClass = borderVisible.value
    ? `border border-${tColor}-${tVariant}`
    : 'border border-transparent'

  const styleClasses = {
    default: `${baseClasses} bg-${bgColor}-${bgVariant} hover:bg-${hoverColor}-${hoverVariant} text-${tColor}-${tVariant} rounded-md ${borderClass}`,
    rounded: `${baseClasses} bg-${bgColor}-${bgVariant} hover:bg-${hoverColor}-${hoverVariant} text-${tColor}-${tVariant} rounded-full ${borderClass}`,
    square: `${baseClasses} bg-${bgColor}-${bgVariant} hover:bg-${hoverColor}-${hoverVariant} text-${tColor}-${tVariant} ${borderClass}`,
    outline: `${baseClasses} bg-transparent hover:bg-${bgColor}-${bgVariant} text-${bgColor}-${bgVariant} hover:text-${tColor}-${tVariant} rounded-md border border-${bgColor}-${bgVariant}`,
    'outline-rounded': `${baseClasses} bg-transparent hover:bg-${bgColor}-${bgVariant} text-${bgColor}-${bgVariant} hover:text-${tColor}-${tVariant} rounded-full border border-${bgColor}-${bgVariant}`,
    primary: `btn-style-primary-color-${backgroundColor.value}`,
    secondary: `btn-style-secondary-color-${backgroundColor.value}`,
    white: ``,
    transparent: `btn-style-transparent-color-${backgroundColor.value}`,
    'white-colored': `btn-style-white-colored-color-${backgroundColor.value}`,
  }

  return styleClasses[style]
}

const pageContext = usePageContext()
useHead(() => {
  const buttonColor = props.item.color || 'default'
  const buttonText = props.item.textColor
  // We need to set CSS vars inside <head><style> instead of using v-bind() directly inside <style scoped>
  // because v-bind generates style="..." and that's not allowed with CSP enabled.
  const style = [
    {
      key: props.item._uid,
      nonce: pageContext.nonce,
      innerHTML: `
          #button${props.item._uid} {
            --btn-color: var(--${colorCustomClass(buttonColor, null, '')});
            --btn-text: var(--${colorCustomClass(buttonText, buttonColor, '')});
            --btn-border: var(--${colorCustomClass(borderVisible.value ? buttonText : 'transparent', buttonColor, '')});
          }
      `,
    },
  ]

  return {
    style,
  }
})
</script>
